import {FormSection} from '@/components/commons/form-section/FormSection'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputSelectPatient} from '@/features/patients/components/input-select-patient/InputSelectPatient'
import {PatientSection} from '@/features/patients/components/patient-section/PatientSection'
import {useGetPatient} from '@/features/patients/services/useGetPatient'
import {PatientSmall} from '@/features/patients/types'
import {ProjectFormWarningBanner} from '@/features/projects/components/project-form/project-form-warning-banner/ProjectFormWarningBanner'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {CreateProjectUrlParams, ProjectCreationError} from '@/features/projects/types'
import {checkProjectCreationEligibility} from '@/features/projects/utils'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {useTranslation} from '@/translations/i18n'
import {useEffect} from 'react'
import {useController, useFormContext} from 'react-hook-form'
import {z} from 'zod'

const ProjectFormPatientSchema = ProjectFieldsSchema.pick({
    patient: true,
    sector: true,
    treatmentRequestId: true
})
type ProjectFormPatientSchema = z.infer<typeof ProjectFormPatientSchema>

interface ProjectFormPatientSectionProps {
    showPatientSelect?: boolean
    error?: ProjectCreationError | null
    setError?: (error: ProjectCreationError | null) => void
    patientId?: string
}

export const ProjectFormPatientSection = ({
    showPatientSelect,
    error,
    setError,
    patientId
}: ProjectFormPatientSectionProps) => {
    const {t} = useTranslation()
    const {searchParams, setSearchParams} = useParsedSearchParams(CreateProjectUrlParams)
    const {control, reset} = useFormContext<ProjectFormPatientSchema>()
    const {field: patientField, fieldState: patientFieldState} = useController({control, name: 'patient'})

    const currentPatientId = searchParams.patientId ?? patientId
    const {data: patient, isLoading: isLoadingPatient} = useGetPatient({
        patientId: currentPatientId ?? undefined,
        responseType: 'extended',
        options: {
            enabled: !!currentPatientId
        }
    })

    // Check if the patient is eligible for project creation
    useEffect(() => {
        if (patient) {
            checkProjectCreationEligibility(patient, setError)
        }
    }, [patient, setError])

    // Reset the patient value when the patient changes
    const handlePatientChange = (patient: PatientSmall | null) => {
        if (patient) {
            reset({
                treatmentRequestId: patient.treatmentRequestId ?? '',
                patient: {
                    id: patient.id,
                    fullName: patient.fullName,
                    serviceType: patient.serviceType
                }
            })
            setSearchParams({patientId: patient.id})
        }
    }

    return (
        <FormSection title={t('projects:patient_info')}>
            <Flexbox direction="column" gap={4}>
                {showPatientSelect && (
                    <InputSelectPatient
                        label={t('commons:patient')}
                        placeholder={isLoadingPatient ? t('commons:loading') : t('commons:select_patient')}
                        errorMessage={patientFieldState.error?.message}
                        value={patientField.value}
                        onChange={handlePatientChange}
                        disabled={isLoadingPatient}
                        isLoading={isLoadingPatient}
                    />
                )}
                {currentPatientId && <PatientSection patientId={currentPatientId} />}
                {error && <ProjectFormWarningBanner status={patient?.project?.status || undefined} error={error} />}
            </Flexbox>
        </FormSection>
    )
}
